import React from 'react';

import { TOAST_ANIM } from '../theme/tokens';
import type { ToastData, ToastOptions } from '../components/Toast/Toast';

export type UseToast = {
  /** The current toast — pass straight to `<Toast toast={toast} />`, or null. */
  toast: ToastData | null;
  /** Show a toast with the given copy (auto-dismisses after the hold). */
  show: (message: string, options?: ToastOptions) => void;
  /** Dismiss the current toast immediately (before the auto-dismiss fires). */
  dismiss: () => void;
};

/**
 * State + auto-dismiss timing for the `<Toast>` component. Keeps the toast as a
 * declarative value (the component owns the enter/exit animation): `show()`
 * sets it and starts the hold timer; the timer (or `dismiss()`) clears it, and
 * the component then plays its exit. Each `show()` bumps a token so re-showing
 * identical copy still re-fires, and resets the hold so rapid copies keep the
 * toast up with the latest message.
 *
 *   const { toast, show } = useToast();
 *   // ...
 *   show('Host copied to clipboard');
 *   // ...
 *   <Toast toast={toast} />
 */
export const useToast = (): UseToast => {
  const [toast, setToast] = React.useState<ToastData | null>(null);
  const seq = React.useRef(0);
  const holdTimer = React.useRef<number | undefined>(undefined);

  const dismiss = React.useCallback(() => {
    window.clearTimeout(holdTimer.current);
    setToast(null);
  }, []);

  const show = React.useCallback((message: string, options?: ToastOptions) => {
    window.clearTimeout(holdTimer.current);
    seq.current += 1;
    setToast({ ...options, message, token: seq.current });
    holdTimer.current = window.setTimeout(() => setToast(null), TOAST_ANIM.HOLD_MS);
  }, []);

  React.useEffect(() => () => window.clearTimeout(holdTimer.current), []);

  return { toast, show, dismiss };
};

export default useToast;
